import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import AppLayout from "@/components/AppLayout";
import Landing from "@/pages/Landing";
import Policies from "@/pages/Policies";
import Login from "@/pages/Login";
import Welcome from "@/pages/Welcome";
import SetupPassword from "@/pages/SetupPassword";
import Dashboard from "@/pages/Dashboard";
import QuotesList from "@/pages/QuotesList";
import QuoteEditor from "@/pages/QuoteEditor";
import PublicQuote from "@/pages/PublicQuote";
import ClientsList from "@/pages/ClientsList";
import CompanyConfig from "@/pages/CompanyConfig";
import UsersList from "@/pages/UsersList";
import MyAccount from "@/pages/MyAccount";
import CalendarPage from "@/pages/CalendarPage";
import JobEditor from "@/pages/JobEditor";
import ReportsList from "@/pages/ReportsList";
import ReportEditor from "@/pages/ReportEditor";
import PublicReport from "@/pages/PublicReport";
import ProspectsList from "@/pages/ProspectsList";
import Finance from "@/pages/Finance";
import Catalog from "@/pages/Catalog";
import Prompts from "@/pages/Prompts";

function ProtectedShell() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando…</div>;
  }
  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  }
  return <AppLayout />;
}

function RequirePerm({ perm, children }) {
  const { can, loading } = useAuth();
  if (loading) return null;
  if (!can(perm)) return <div className="pe-card text-center text-slate-600 m-6 p-8" data-testid="no-permission">No tienes permiso para acceder a esta sección.</div>;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/politicas" element={<Policies />} />
          <Route path="/policies" element={<Policies />} />
          <Route path="/login" element={<Login />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/setup-password" element={<SetupPassword />} />
          <Route path="/c/:token" element={<PublicQuote />} />
          <Route path="/p/quote/:token" element={<PublicQuote />} />
          <Route path="/r/:token" element={<PublicReport />} />

          <Route element={<ProtectedShell />}>
            <Route path="/dashboard" element={<Dashboard />} />

            {/* Support both Spanish and English routes for convenience */}
            <Route path="/cotizaciones" element={<RequirePerm perm="quotes_read"><QuotesList /></RequirePerm>} />
            <Route path="/cotizaciones/nueva" element={<RequirePerm perm="quotes_write"><QuoteEditor /></RequirePerm>} />
            <Route path="/cotizaciones/:id" element={<RequirePerm perm="quotes_read"><QuoteEditor /></RequirePerm>} />
            <Route path="/quotes" element={<RequirePerm perm="quotes_read"><QuotesList /></RequirePerm>} />
            <Route path="/quotes/new" element={<RequirePerm perm="quotes_write"><QuoteEditor /></RequirePerm>} />
            <Route path="/quotes/:id" element={<RequirePerm perm="quotes_read"><QuoteEditor /></RequirePerm>} />

            <Route path="/clientes" element={<RequirePerm perm="clients_read"><ClientsList /></RequirePerm>} />
            <Route path="/clients" element={<RequirePerm perm="clients_read"><ClientsList /></RequirePerm>} />

            <Route path="/usuarios" element={<RequirePerm perm="users_manage"><UsersList /></RequirePerm>} />
            <Route path="/users" element={<RequirePerm perm="users_manage"><UsersList /></RequirePerm>} />

            <Route path="/configuracion" element={<RequirePerm perm="company_config"><CompanyConfig /></RequirePerm>} />
            <Route path="/company" element={<RequirePerm perm="company_config"><CompanyConfig /></RequirePerm>} />

            <Route path="/mi-cuenta" element={<MyAccount />} />
            <Route path="/account" element={<MyAccount />} />

            {/* Phase 2 — Calendario y Reportes de servicio */}
            <Route path="/calendar" element={<RequirePerm perm="calendar_read"><CalendarPage /></RequirePerm>} />
            <Route path="/agenda" element={<RequirePerm perm="calendar_read"><CalendarPage /></RequirePerm>} />
            <Route path="/jobs/new" element={<RequirePerm perm="calendar_write"><JobEditor /></RequirePerm>} />
            <Route path="/jobs/:id" element={<RequirePerm perm="calendar_read"><JobEditor /></RequirePerm>} />
            <Route path="/reports" element={<RequirePerm perm="reports_read"><ReportsList /></RequirePerm>} />
            <Route path="/reportes" element={<RequirePerm perm="reports_read"><ReportsList /></RequirePerm>} />
            <Route path="/reports/new" element={<RequirePerm perm="reports_write"><ReportEditor /></RequirePerm>} />
            <Route path="/reports/:id" element={<RequirePerm perm="reports_read"><ReportEditor /></RequirePerm>} />

            {/* Prospects (landing leads) — admin + vendedor */}
            <Route path="/prospects" element={<ProspectsList />} />
            <Route path="/prospectos" element={<ProspectsList />} />

            {/* Finanzas — Módulo 5 (permisos finance_read / finance_write) */}
            <Route path="/finance" element={<RequirePerm perm="finance_read"><Finance /></RequirePerm>} />
            <Route path="/finanzas" element={<RequirePerm perm="finance_read"><Finance /></RequirePerm>} />

            {/* Catálogo — Sección 9.11 PRD (catalog_read / catalog_write) */}
            <Route path="/catalog" element={<RequirePerm perm="catalog_read"><Catalog /></RequirePerm>} />
            <Route path="/catalogo" element={<RequirePerm perm="catalog_read"><Catalog /></RequirePerm>} />

            {/* Prompts — PRD v1.11 (bloc de notas / portapapeles técnico) */}
            <Route path="/prompts" element={<Prompts />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
